if (!customElements.get('subscription-popup')) {
  class SubscriptionOfferPopup extends HTMLElement {
    constructor() {
      super();

      this.offerContext = {};
      this.isThemeEditorPreview = false;
      this.closeButton = this.querySelector('[data-role="close"]');
      this.submitButton = this.querySelector('[data-role="submit"]');
      this.frequencyButtons = Array.from(this.querySelectorAll('[data-frequency]'));
      this.additionalProductCards = Array.from(this.querySelectorAll('[data-additional-product-variant-id]'));
      this.giftValueCards = Array.from(this.querySelectorAll('[data-gift-compare-price-cents]'));
      this.currentPriceElement = this.querySelector('[data-popup-current-price]');
      this.comparePriceElement = this.querySelector('[data-popup-compare-price]');
      this.savingsElement = this.querySelector('[data-popup-savings]');
      this.discountBadgeElement = this.querySelector('[data-popup-current-discount-badge]');
      this.templateElements = Array.from(this.querySelectorAll('[data-popup-template]'));
      this.templateElements.forEach((element) => {
        element.dataset.popupTemplateHtml = element.innerHTML;
      });
      this.selectedFrequency = this.querySelector('[data-frequency].active')?.dataset.frequency || this.frequencyButtons[0]?.dataset.frequency || null;

      this.handleOpen = this.handleOpen.bind(this);
      this.handleLoading = this.handleLoading.bind(this);
      this.handleDecline = this.handleDecline.bind(this);
      this.handleAccept = this.handleAccept.bind(this);
      this.handleThemeEditorSectionSelect = this.handleThemeEditorSectionSelect.bind(this);
      this.handleThemeEditorSectionDeselect = this.handleThemeEditorSectionDeselect.bind(this);
    }

    connectedCallback() {
      const navEntry = performance.getEntriesByType('navigation')[0];
      if (navEntry && navEntry.type === 'back_forward') {
        window.location.reload();
      }
      
      this.setAttribute('aria-hidden', 'true');
      this.addEventListener('subscription-popup:open', this.handleOpen);
      this.addEventListener('subscription-popup:set-loading', this.handleLoading);
      this.closeButton?.addEventListener('click', this.handleDecline);
      this.submitButton?.addEventListener('click', this.handleAccept);

      this.frequencyButtons.forEach((button) => {
        button.addEventListener('click', () => this.selectFrequency(button));
      });

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:load', this.handleThemeEditorSectionSelect);
        document.addEventListener('shopify:section:select', this.handleThemeEditorSectionSelect);
        document.addEventListener('shopify:block:select', this.handleThemeEditorSectionSelect);
        document.addEventListener('shopify:section:deselect', this.handleThemeEditorSectionDeselect);
        document.addEventListener('shopify:block:deselect', this.handleThemeEditorSectionDeselect);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('subscription-popup:open', this.handleOpen);
      this.removeEventListener('subscription-popup:set-loading', this.handleLoading);
      this.closeButton?.removeEventListener('click', this.handleDecline);
      this.submitButton?.removeEventListener('click', this.handleAccept);

      if (window.Shopify?.designMode) {
        document.removeEventListener('shopify:section:load', this.handleThemeEditorSectionSelect);
        document.removeEventListener('shopify:section:select', this.handleThemeEditorSectionSelect);
        document.removeEventListener('shopify:block:select', this.handleThemeEditorSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.handleThemeEditorSectionDeselect);
        document.removeEventListener('shopify:block:deselect', this.handleThemeEditorSectionDeselect);
      }
    }

    handleOpen(event) {
      this.offerContext = event.detail || {};
      this.renderOfferContext();
      this.open();
    }

    handleLoading(event) {
      this.setLoading(Boolean(event.detail?.isLoading));
    }

    handleDecline() {
      if (this.classList.contains('is-loading')) return;

      if (this.isThemeEditorPreview) {
        this.closeThemeEditorPreview();
        return;
      }

      this.addOfferToCart('default');
      this.dispatchEvent(new CustomEvent('subscription-popup:decline', {
        bubbles: true,
        composed: true,
        detail: this.getEventDetail(),
      }));
    }

    handleAccept() {
      if (this.classList.contains('is-loading')) return;

      if (this.isThemeEditorPreview) {
        this.closeThemeEditorPreview();
        return;
      }

      this.addOfferToCart('subscription');
      this.dispatchEvent(new CustomEvent('subscription-popup:accept', {
        bubbles: true,
        composed: true,
        detail: this.getEventDetail(),
      }));
    }

    handleThemeEditorSectionSelect(event) {
      if (!this.isCurrentThemeEditorSection(event)) return;

      this.openThemeEditorPreview();
    }

    handleThemeEditorSectionDeselect(event) {
      if (!this.isCurrentThemeEditorSection(event)) return;

      this.closeThemeEditorPreview();
    }

    isCurrentThemeEditorSection(event) {
      if (event.target?.contains(this)) return true;

      const popupSection = this.closest('[id^="shopify-section-"]');
      const eventSection = event.target?.closest?.('[id^="shopify-section-"]');

      return Boolean(popupSection && eventSection && popupSection === eventSection);
    }

    openThemeEditorPreview() {
      this.isThemeEditorPreview = true;

      const sectionElement = this.closest('landing-product-double');
      if (typeof sectionElement?.getActiveOfferContext === 'function') {
        this.offerContext = sectionElement.getActiveOfferContext();
        this.renderOfferContext();
      }

      this.open();
    }

    closeThemeEditorPreview() {
      this.isThemeEditorPreview = false;
      this.close();
    }

    renderOfferContext() {
      const currentDiscountPercent = this.getCurrentDiscountPercent();
      const popupPrices = this.getPopupPriceValues(currentDiscountPercent);

      if (this.currentPriceElement) {
        this.currentPriceElement.textContent = this.formatMoney(popupPrices.currentPrice, true);
      }

      if (this.comparePriceElement) {
        this.comparePriceElement.textContent = this.formatMoney(popupPrices.comparePrice, true);
      }

      if (this.savingsElement) {
        const savingsText = this.getSavingsHtml(currentDiscountPercent, popupPrices);
        this.savingsElement.innerHTML = savingsText;
        this.savingsElement.hidden = !savingsText;
      }

      if (this.discountBadgeElement) {
        this.discountBadgeElement.textContent = this.replaceTemplateValues(
          this.dataset.saveBadgeTemplate || '',
          currentDiscountPercent
        );
      }

      this.renderTemplateElements(currentDiscountPercent);
    }

    renderTemplateElements(currentDiscountPercent) {
      const futureDiscountPercent = this.getFutureDiscountPercent();

      this.templateElements.forEach((element) => {
        const templateHtml = element.dataset.popupTemplateHtml || '';
        element.innerHTML = this.replaceTemplateValues(templateHtml, currentDiscountPercent, futureDiscountPercent);
      });
    }

    replaceTemplateValues(templateHtml, currentDiscountPercent, futureDiscountPercent = this.getFutureDiscountPercent(), prices = {}) {
      return templateHtml
        .replace(/\[curent_discount_percent\]/g, `${currentDiscountPercent}%`)
        .replace(/\[current_discount_percent\]/g, `${currentDiscountPercent}%`)
        .replace(/\[future_discount_percent\]/g, `${futureDiscountPercent}%`)
        .replace(/\[total_discount\]/g, prices.totalDiscount || '')
        .replace(/\[gifts_total\]/g, prices.giftsTotal || '');
    }

    getPopupPriceValues(currentDiscountPercent = this.getCurrentDiscountPercent()) {
      const comparePrice = this.parseMoney(this.offerContext.priceCompare || this.offerContext.priceOriginal);
      const currentPrice = comparePrice * (100 - currentDiscountPercent) / 100;

      return {
        currentPrice: Number.isFinite(currentPrice) ? currentPrice : 0,
        comparePrice: Number.isFinite(comparePrice) ? comparePrice : 0,
      };
    }

    getSavingsHtml(currentDiscountPercent = this.getCurrentDiscountPercent(), popupPrices = this.getPopupPriceValues(currentDiscountPercent)) {
      const currentPrice = popupPrices.currentPrice;
      const comparePrice = popupPrices.comparePrice;
      const mainDiscount = Math.max(comparePrice - currentPrice, 0);
      const giftsComparePrice = this.getGiftsComparePrice();
      const totalDiscount = mainDiscount + giftsComparePrice;

      if (!totalDiscount) return '';

      if (giftsComparePrice > 0) {
        return this.replaceTemplateValues(
          this.dataset.savingsWithGiftsTemplate || '',
          currentDiscountPercent,
          this.getFutureDiscountPercent(),
          {
            totalDiscount: this.formatMoney(totalDiscount),
            giftsTotal: this.formatMoney(giftsComparePrice),
          }
        );
      }

      return this.replaceTemplateValues(
        this.dataset.savingsWithoutGiftsTemplate || '',
        currentDiscountPercent,
        this.getFutureDiscountPercent(),
        {
          totalDiscount: this.formatMoney(totalDiscount),
        }
      );
    }

    getCurrentDiscountPercent() {
      const settings = this.offerContext.settings || {};
      const quantity = Number(this.offerContext.quantity || 0);
      const productType = this.offerContext.productType || this.offerContext.product?.type;
      const bigQuantity = Number(settings.quantity_big || 0);
      const isBigPack = bigQuantity ? quantity >= bigQuantity : quantity > 3;
      const useBigDiscount = productType === 'Pod' ? quantity > 2 : isBigPack;
      const discountPercent = useBigDiscount
        ? settings.subscription_popup_discount_percent_big
        : settings.subscription_popup_discount_percent;

      return Number(discountPercent || settings.subscription_popup_discount_percent || 0);
    }

    getFutureDiscountPercent() {
      const settings = this.offerContext.settings || {};
      const productId = this.offerContext.productId;
      const discounts = this.getSubscriptionDiscounts(settings.product_subscription_discounts?.[productId] || this.offerContext.product?.volume_discounts_subs);
      const currentVariant = this.getCurrentVariant();
      const originalPrice = Number(currentVariant?.compare_at_price || currentVariant?.price || 0);
      const quantity = Number(this.offerContext.quantity || 0);

      if (!discounts.length || !originalPrice) return 0;

      const discount = this.getVolumeDiscountForQuantity(discounts, quantity);
      const discountedPrice = this.getVolumeDiscountPrice(discount);

      if (!discountedPrice || discountedPrice >= originalPrice) return 0;

      return Math.round((originalPrice - discountedPrice) / originalPrice * 100);
    }

    getSubscriptionDiscounts(discounts) {
      if (Array.isArray(discounts)) return discounts;
      if (!discounts) return [];

      try {
        const parsedDiscounts = JSON.parse(discounts);
        if (Array.isArray(parsedDiscounts)) return parsedDiscounts;
      } catch (error) {
        console.error('Failed to parse subscription discounts:', error);
      }

      return String(discounts)
        .split(',')
        .map((discount) => discount.trim())
        .filter(Boolean);
    }

    getCurrentVariant() {
      return (this.offerContext.product?.variants || []).find((variant) => {
        return Number(variant.id) === Number(this.offerContext.variantId)
          || variant.title === this.offerContext.variantTitle;
      });
    }

    getVolumeDiscountForQuantity(discounts, quantity) {
      const lastDiscount = discounts[discounts.length - 1];
      const exactDiscount = discounts.find((discount) => {
        return Number(this.getVolumeDiscountQuantity(discount)) === quantity;
      });

      return exactDiscount || lastDiscount;
    }

    getVolumeDiscountQuantity(discount) {
      const discountString = String(discount || '');
      return discountString.split('--')[0]?.replace('+', '') || 0;
    }

    getVolumeDiscountPrice(discount) {
      const discountString = String(discount || '');
      return Math.round(Number(discountString.split('--')[1] || 0) * 100);
    }

    getGiftsComparePrice() {
      return this.giftValueCards.reduce((total, card) => {
        return total + (Number(card.dataset.giftComparePriceCents || 0) / 100);
      }, 0);
    }

    parseMoney(value) {
      if (!value) return 0;

      return Number(String(value).replace(/[^0-9.]/g, ''));
    }

    formatMoney(value, noTrailingZeros = false) {
      const amount = Number(value || 0);
      const formattedAmount = noTrailingZeros ? amount.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1') : amount.toFixed(2);
      const moneyFormat = window.theme?.moneyFormat;

      if (moneyFormat?.includes('{{amount}}')) {
        return moneyFormat.replace('{{amount}}', formattedAmount);
      }

      return `$${formattedAmount}`;
    }

    selectFrequency(selectedButton) {
      this.frequencyButtons.forEach((button) => {
        const isActive = button === selectedButton;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      this.selectedFrequency = selectedButton.dataset.frequency;
    }

    getEventDetail() {
      return {
        ...this.offerContext,
        frequency: this.selectedFrequency,
        sellingPlan: this.getSelectedSellingPlan(),
        additionalItems: this.getAdditionalItems(),
      };
    }

    getSelectedSellingPlan() {
      const sellingPlans = (this.offerContext.product?.selling_plan_groups || [])
        .flatMap((group) => group.selling_plans || []);
      const selectedFrequencyNumber = this.selectedFrequency?.match(/\d+/)?.[0];

      if (!sellingPlans.length) return null;

      if (selectedFrequencyNumber) {
        const frequencyPlan = sellingPlans.find((plan) => {
          const searchablePlan = [
            plan.name,
            ...(plan.options || []).map((option) => option.value || '')
          ].join(' ').toLowerCase();

          return searchablePlan.includes(`every ${selectedFrequencyNumber} week`)
            || searchablePlan.includes(`${selectedFrequencyNumber} week`)
            || searchablePlan.includes(`${selectedFrequencyNumber} week(s)`)
            || searchablePlan.includes(selectedFrequencyNumber);
        });

        if (frequencyPlan) return frequencyPlan;
      }

      return sellingPlans[0] || null;
    }

    getAdditionalItems() {
      return this.additionalProductCards
        .map((card) => Number(card.dataset.additionalProductVariantId))
        .filter(Boolean)
        .map((id) => ({ id, quantity: 1 }));
    }

    async addOfferToCart(mode) {
      const detail = this.getEventDetail();
      const mainItem = this.buildMainItem(detail, mode);

      if (!mainItem?.id) {
        this.setLoading(false);
        return;
      }

      this.setLoading(true);

      const items = [mainItem];

      if (mode === 'subscription' && detail.settings?.enable_subscription_popup_additional_products) {
        items.push(...this.buildAdditionalItems(detail, mainItem));
      }

      try {
        await fetch('/cart/clear.js');
        await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const checkoutUrl = mode === 'subscription'
          ? detail.subscriptionCheckoutUrl || detail.checkoutUrl || '/checkout'
          : detail.checkoutUrl || '/checkout';

        window.location.href = checkoutUrl;
      } catch (error) {
        this.setLoading(false);
        console.error('Subscription offer add to cart error:', error);
      }
    }

    buildMainItem(detail, mode) {
      const item = {
        id: Number(detail.variantId),
        quantity: Number(detail.quantity || 1),
      };
      const properties = {};

      if (mode === 'subscription') {
        const sellingPlan = detail.sellingPlan || this.getSelectedSellingPlan();
        if (sellingPlan?.id) {
          item.selling_plan = sellingPlan.id;
        }
      }

      if (detail.landingUniqProp) {
        properties._landingSubscription = detail.landingUniqProp;
      }

      if (detail.firstVisitedPage) {
        properties._first_visited_page = detail.firstVisitedPage;
      }

      if (Object.keys(properties).length) {
        item.properties = properties;
      }

      return item;
    }

    buildAdditionalItems(detail, mainItem) {
      return detail.additionalItems.map((additionalItem) => {
        const item = {
          id: Number(additionalItem.id),
          quantity: Number(additionalItem.quantity || 1),
          parent_id: mainItem.id,
        };
        const properties = {};

        if (detail.settings?.subscription_popup_additional_products_is_free) {
          properties._landing_gift = mainItem.id;
        }

        if (detail.landingUniqProp) {
          properties._landingSubscription = detail.landingUniqProp;
        }

        if (Object.keys(properties).length) {
          item.properties = properties;
        }

        return item;
      });
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.submitButton?.toggleAttribute('disabled', isLoading);
      this.closeButton?.toggleAttribute('disabled', isLoading);
      this.frequencyButtons.forEach((button) => button.toggleAttribute('disabled', isLoading));
    }

    open() {
      this.classList.add('active');
      this.setAttribute('aria-hidden', 'false');
      this.setLoading(false);
      document.body.classList.add('subscription-open');
    }

    close() {
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      this.setLoading(false);
      document.body.classList.remove('subscription-open');
    }
  }

  customElements.define('subscription-popup', SubscriptionOfferPopup);
}
 